import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Alert, Button, Menu, Modal, Tooltip } from "@mantine/core";
import { bindActionCreators } from "redux";
import {
  deleteAvailability,
  getAvailabilities
} from "../../data/actions/availabilityActions";
import { IState } from "../../data/reducers/state";
import { IAvailability } from "../../types/availabilities/Availabilities";
import { IRecurringAvailability } from "../../types/availabilities/RecurringAvailability";
import { IPerson } from "../../types/persons/person";
import { IVenue } from "../../types/venue/Venue";
import { toDateTime, toHumanDuration } from "../../util/date+helper";
import CreateAvailabilityForm from "../availabilities/CreateAvailabilityForm";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";
import { IProfileTabProps } from "./ProfileTabType";

export interface IAvailabilityHistoryProps extends IProfileTabProps {
  personId?: string | null;
  availabilities?: IAvailability[];
  venues: { [venueId: string]: IVenue };
  canEdit?: boolean;
  isUnavailableByDefault?: boolean;
  isSelf: boolean;
  relevantTime?: DateTime;
  shiftContext?: string;

  actions?: {
    getAvailabilities: (person: string, options: { shiftContext?: string, start?: DateTime }) => Promise<IAvailability[]>;
    deleteAvailability: (avId: string) => Promise<IAvailability>;
  };
}

interface IAvailabilityListProps {
  availabilities: IAvailability[];
  deleteAvailability: Function;
  venueMap: { [venueId: string]: IVenue };
  canEdit: boolean;
  person: IPerson;
  isSelf: boolean;
  typeText: string;
}

export const rowColourFromAvailability: (
  availability: IAvailability,
) => string = (avail) =>
    avail.available ? "var(--color-success-bg)" : "var(--color-danger-bg)";

export const AvailabilityDateElement: React.FunctionComponent<{
  availability: IAvailability;
  id?: string;
}> = ({ availability, id, ...rest }) => {
  const startDate = toDateTime(availability.start);
  const endDate = toDateTime(availability.finish);

  const durationDays = Math.ceil(
    Math.abs(startDate.diff(endDate, "days").days),
  );

  const FORMAT_DATE = "dd MMMM";
  const FORMAT_DATE_AND_YEAR = "dd MMMM yyyy";

  let date: string = "";
  if (!startDate.hasSame(endDate, "year")) {
    const start = startDate.toFormat(FORMAT_DATE_AND_YEAR);
    const finish = endDate.toFormat(FORMAT_DATE_AND_YEAR);
    date = `${start} - ${finish}`;
  } else if (startDate.hasSame(endDate, "month")) {
    if (startDate.hasSame(endDate, "day")) {
      date = startDate.toFormat(FORMAT_DATE);
    } else {
      date = `${startDate.day} - ${endDate.day} ${startDate.toFormat("MMM")}`;
    }
  } else {
    const start = startDate.toFormat(FORMAT_DATE);
    const finish = endDate.toFormat(FORMAT_DATE);
    date = `${start} - ${finish}`;
    if (!DateTime.local().hasSame(startDate, "year")) {
      date += ` ${startDate.year}`;
    }
  }
  let extra;
  if (durationDays > 1) {
    const nDay =
      durationDays > 1 ? `${durationDays} days` : `${durationDays} day`;

    extra = (
      <>
        <br />
        (for {nDay})
      </>
    );
  }

  return (
    <span {...rest} id={id}>
      {date}
      {extra}
    </span>
  );
};

export const AvailabilityDurationElement: React.FunctionComponent<{
  availability: IAvailability;
}> = ({ availability }) => {
  const startDate = toDateTime(availability.start);
  const endDate = toDateTime(availability.finish);
  const day = { year: 2000, month: 1, day: 1 };
  const stime = startDate.set(day);
  const ftime = endDate.set(day);
  const { seconds } = ftime.diff(stime, "seconds");
  const timeDiff = toHumanDuration(seconds);

  // 1 day ish
  if (seconds > 86000) {
    return (
      <span>
        <em>All day</em>
      </span>
    );
  }

  return (
    <span>
      {startDate.toFormat("hh:mm a ")} -{endDate.toFormat("hh:mm a ")}
      <br />
      <em>{timeDiff}</em>
    </span>
  );
};

const RecurringAvailabilityList: React.FunctionComponent<
  IAvailabilityListProps
> = ({
  availabilities,
  deleteAvailability,
  canEdit,
  isSelf,
  person,
  typeText,
  venueMap,
}) => {
    const name = isSelf
      ? "You haven't"
      : `${person?.preferredName || person?.firstName} hasn't`;
    if (availabilities.length == 0) {
      return (
        <p style={{ marginTop: "20px", textAlign: "center", color: "darkgray" }}>
          {name} scheduled any {typeText.toLowerCase()} yet!
        </p>
      );
    }
    return (
      <SSTable striped={false}>
        <thead>
          <tr>
            <th>Type</th>
            <th>Date</th>
            <th>Start</th>
            <th>Recurring</th>
            <th>Venues</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {availabilities?.map((a) => {
            const availOrNot = a.available ? "Available" : "Unavailable";
            // set by the sever as a special case
            // @ts-ignore
            const every = a?.recurrence?.text;
            const tooltipId = _.uniqueId(`tooltip-${a._id}-timestamp`);

            let venues =
              a.venues && a.venues.length > 0
                ? `${a.venues.length} venue`
                : "ALL";

            if (
              (a.venues?.length || 0) > 0 &&
              a.venues?.filter((v) => v in venueMap).length === a.venues?.length
            ) {
              venues =
                a.venues?.map((v) => venueMap[v]?.name || "").join(", ") ||
                venues;
            }

            const cellStyle = { backgroundColor: rowColourFromAvailability(a) };

            return (
              <tr
                key={a._id}
                style={cellStyle}
              >
                <td style={cellStyle}>{a.available ? "" : a.type}</td>
                <td style={cellStyle}>
                  <Tooltip
                    label={`Created on ${toDateTime(a.timestamp).toLocaleString(
                      DateTime.DATETIME_FULL,
                    )}`}
                    position="bottom"
                  >
                    <span>
                      <AvailabilityDateElement id={tooltipId} availability={a} />
                    </span>
                  </Tooltip>
                </td>
                <td style={cellStyle}>
                  <AvailabilityDurationElement availability={a} />
                </td>
                <td style={cellStyle}>
                  {availOrNot} {every}
                </td>
                <td style={cellStyle}>{venues}</td>
                {canEdit && (
                  <td style={cellStyle}>
                    {" "}
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <button style={{ border: "none", background: "none", cursor: "pointer" }}>
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Label>Actions</Menu.Label>
                        <Menu.Item onClick={deleteAvailability(a._id)}>
                          Delete availability
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                    {/* <button style={{ border: "none" }} onClick={toggleModal(a._id)}></button> */}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </SSTable>
    );
  };

const RegularAvailabilityList: React.FunctionComponent<
  IAvailabilityListProps
> = ({
  availabilities,
  deleteAvailability,
  venueMap,
  canEdit,
  isSelf,
  person,
  typeText,
}) => {
    const name = isSelf
      ? "You haven't"
      : `${person?.preferredName || person?.firstName} hasn't`;

    if (availabilities.length == 0) {
      return (
        <p style={{ marginTop: "20px", textAlign: "center", color: "darkgray" }}>
          {name} scheduled any {typeText.toLowerCase()} yet!
        </p>
      );
    }

    return (
      <SSTable>
        <thead>
          <tr>
            <th>Type</th>
            <th>Start</th>
            <th>Finish</th>
            <th>Venues</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {availabilities?.map((a) => {
            const startDate = toDateTime(a.start);
            const endDate = toDateTime(a.finish);

            const fullFormat = (date: DateTime) =>
              date.toFormat(
                DateTime.local().hasSame(date, "year")
                  ? "EEEE, dd MMMM hh:mm a "
                  : "EEEE, dd MMMM yyyy hh:mm a ",
              );

            const start = fullFormat(startDate);
            let finish = endDate.toFormat("hh:mm a");

            if (!startDate.hasSame(endDate, "day")) {
              finish = fullFormat(endDate);
            }

            let venues =
              a.venues && a.venues.length > 0
                ? `${a.venues.length} venue`
                : "ALL";
            if (
              (a.venues?.length || 0) > 0 &&
              a.venues?.filter((v) => v in venueMap).length === a.venues?.length
            ) {
              venues =
                a.venues?.map((v) => venueMap[v]?.name || "").join(", ") ||
                venues;
            }

            const availableOrNot = a.available ? "Available" : "Unavailable";
            const cellStyle = { backgroundColor: rowColourFromAvailability(a) };

            return (
              <tr
                key={a._id}
                style={cellStyle}
              >
                <td style={cellStyle}>{a.available ? "" : a.type}</td>
                <td style={cellStyle}>{start}</td>
                <td style={cellStyle}>{finish}</td>
                <td style={cellStyle}>
                  {availableOrNot} for {' '}
                  {venues}
                </td>
                {canEdit && (
                  <td style={cellStyle}>
                    {" "}
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <button style={{ border: "none", background: "none", cursor: "pointer" }}>
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Label>Actions</Menu.Label>
                        <Menu.Item onClick={deleteAvailability(a._id)}>
                          Delete availability
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </SSTable>
    );
  };

const AvailabilityHistory: React.FC<IAvailabilityHistoryProps> = (props) => {
  interface ICreateAvailPopupSettings {
    show: boolean;
    isRecurring?: boolean;
    availableByDefault?: boolean;
  }

  const [error, setError] = React.useState<string | null>(null);
  const [availabilities, setAvailabilities] = React.useState<IAvailability[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [createAvailSettings, setCreateAvailSettings] =
    React.useState<ICreateAvailPopupSettings>({
      show: false,
      availableByDefault: props.isUnavailableByDefault || false,
    });

  const canEdit = props.canEdit ?? true;

  let inner = <CenteredLoader />;

  if (error) return <Alert color="red">{error}</Alert>;
  if (!props.personId) return <Alert color="red">No person ID</Alert>;

  if (props.isActive === false) return <div />;

  const toggle = () =>
    setCreateAvailSettings({
      ...createAvailSettings,
      show: !createAvailSettings.show,
    });

  const loadAvailabilities = () => {
    if (!props.personId || !props.actions) return;
    setIsLoading(true);
    props.actions
      .getAvailabilities(props.personId, { start: props.relevantTime, shiftContext: props.shiftContext })
      .then((avs) => {
        setAvailabilities(avs);
        setIsLoading(false)
      })
      .catch((er) => {
        setError(er.message);
        setIsLoading(false);
      });
  }

  React.useEffect(() => {
    if (availabilities.length > 0) return;
    console.log('Loading', props.personId, props.relevantTime)
    loadAvailabilities();
  }, [props.personId, props.relevantTime]);

  const recurringAvails = _.orderBy(
    availabilities.filter((a) => a.isRecurring) as IRecurringAvailability[],
    (a) => a.start,
  );
  const regularAvails = _.orderBy(
    availabilities.filter((a) => !a.isRecurring),
    (a) => a.start,
  );

  const deleteAvailabilityWrapper = (availabilityId: string) => () => {
    if (!props.actions) return;
    setError(null);
    props.actions
      .deleteAvailability(availabilityId)
      .then((_) => window.location.reload())
      .catch((er) => setError(er.message));
  };

  const otherProps = {
    deleteAvailability: deleteAvailabilityWrapper,
    isSelf: props.isSelf,
    person: props.person,
    canEdit,
  };

  const recurringAvailSectionText = (props.isUnavailableByDefault ?? false)
    ? "Default availability"
    : "Recurring availabilities";

  const addRecurringAvailButtonText = (props.isUnavailableByDefault ?? false)
    ? "Add default availability"
    : "Add recurring availability";

  const regularAvailSectionText = (props.isUnavailableByDefault ?? false)
    ? "Custom availabilities"
    : "Regular availabilities";

  const regularAvailButtonText = (props.isUnavailableByDefault ?? false)
    ? "Add custom availability"
    : "Add regular availability";

  const isDateToday = props.relevantTime?.hasSame(DateTime.local(), "day");

  inner = (
    <>
      {props.relevantTime && !isDateToday && (
        <Alert color="blue">
          Note: Showing availability relevant for {props.relevantTime?.toLocaleString(DateTime.DATE_FULL)}
        </Alert>
      )}
      <div>
        <div style={{ display: "flex", marginBottom: "10px" }}>
          <p style={{ verticalAlign: "middle", margin: "5px 0 0 0" }}>
            <b>{recurringAvailSectionText}</b>
          </p>
          {canEdit && (
            <Button
              style={{ margin: "0 0 0 auto" }}
              size="sm"
              onClick={() =>
                setCreateAvailSettings({
                  show: true,
                  isRecurring: true,
                  availableByDefault: props.isUnavailableByDefault ?? false,
                })
              }
            >
              {addRecurringAvailButtonText}
            </Button>
          )}
        </div>
        <RecurringAvailabilityList
          venueMap={props.venues}
          availabilities={recurringAvails}
          typeText={recurringAvailSectionText}
          {...otherProps}
        />
      </div>
      <div>
        <div style={{ display: "flex", marginBottom: "10px" }}>
          <p style={{ verticalAlign: "middle", margin: "5px 0 0 0" }}>
            <b>{regularAvailSectionText}</b>
          </p>
          {canEdit && (
            <Button
              style={{ margin: "0 0 0 auto" }}
              size="sm"
              onClick={() =>
                setCreateAvailSettings({
                  show: true,
                  isRecurring: false,
                  availableByDefault: props.isUnavailableByDefault ?? false,
                })
              }
            >
              {regularAvailButtonText}
            </Button>
          )}
        </div>
        <RegularAvailabilityList
          availabilities={regularAvails}
          venueMap={props.venues}
          typeText={regularAvailSectionText}
          {...otherProps}
        />
      </div>
    </>
  );


  return (
    <div style={{}}>
      {inner}
      <Modal
        opened={createAvailSettings.show}
        onClose={toggle}
        title="Create change in Availability"
        size="lg"
      >
        <CreateAvailabilityForm
          person={props.personId}
          toggle={toggle}
          onSave={toggle}
          isRecurring={createAvailSettings.isRecurring || false}
        />
      </Modal>
    </div>
  );
}

function mapStateToProps(state: IState, props: IAvailabilityHistoryProps) {
  const personId = props.personId || state.persons.self;
  // const availIds = state.availabilities.byPerson[personId];
  // const avails = availIds?.map((a) => state.availabilities?.map[a]) || [];
  const isSelf = props.personId == state.persons.self;

  return {
    personId: personId,
    person: state.persons.map[personId],
    venues: state.venues.map || {},
    isSelf,
  };
}

function mapDispatchToProps(dispatch: any): Partial<IAvailabilityHistoryProps> {
  return {
    // @ts-ignore
    actions: bindActionCreators(
      { getAvailabilities, deleteAvailability },
      dispatch,
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AvailabilityHistory);
